import type { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { useSessionStreamStore } from '@/store/sessionStream';
import { createLiveBatcher, keyIsLiveFor, listenLiveHealth, parseLiveEvent } from '@/lib/liveInvalidation';

/**
 * Browser-side EventSource lifecycle for the C3.2 Session-state push.
 *
 * One module-level connection per PWA tab. Subscribes to
 * `/api/me/session-events` (server-side in commit d3e8f40), maps push
 * events to React Query cache invalidations, and broadcasts status
 * via the `sessionStream` Zustand store so polling hooks can back off
 * their intervals while push is healthy.
 *
 * Lifecycle gates on the auth store
 * ─────────────────────────────────
 * Subscription pattern (NOT a React hook — runs at module load via
 * `initSessionEventStream` called from main.tsx):
 *
 *   - Initial mount: if `useAuthStore.getState().accessToken` is set
 *     (persisted login), connect immediately.
 *   - Login: auth store transitions null → token; we connect.
 *   - Token refresh: auth store transitions token → newToken; we
 *     disconnect-then-connect so the SSE handshake uses fresh creds.
 *   - Logout: auth store transitions token → null; we disconnect.
 *
 * Why disconnect-then-connect on refresh (and not just let the
 * EventSource keep its old token): the server validates the token
 * once at handshake. The existing connection survives token rotation
 * fine until the server restarts or the connection drops — at which
 * point the EventSource's native auto-reconnect would replay the
 * stale token in the URL and 401. Cleanest path: every fresh token
 * gets a fresh connection.
 *
 * Reconnection strategy
 * ─────────────────────
 * EventSource has built-in auto-reconnect for transient errors
 * (readyState === CONNECTING during the gap). We intervene ONLY when
 * readyState === CLOSED (terminal — server returned 401, malformed
 * response, etc.). In that case we schedule a reconnect with
 * exponential backoff (1s → 2s → 4s → 8s → 16s → 30s max), reset to
 * 1s on the next successful `connected` ack.
 *
 * Cache invalidation
 * ──────────────────
 * Push events fire React Query invalidations against the broad
 * active-session query keys + the specific session-detail key (when
 * the event carries a sessionId). The polling hooks then re-fetch
 * the canonical state from `/sessions/active` — push is a "wake the
 * poller" signal, not a data delivery channel. Two reasons:
 *   1. The server's per-event payload is intentionally lightweight
 *      (just enough to decide whether to invalidate). The actual
 *      session shape lives behind /sessions/active.
 *   2. Re-fetching keeps a single source of truth. If push and pull
 *      ever disagree, pull wins by virtue of being the next render.
 *
 * Failure posture
 * ───────────────
 * Push is a perf optimization. Polling fallback (60s ↔ 4s/15s/10s)
 * carries correctness independently. Any failure here logs + flips
 * the status store to 'error' (consumers re-tighten their intervals)
 * and the reconnect loop keeps trying without bubbling the failure
 * into the UI.
 *
 * Live cards (28 Oct 2026)
 * ────────────────────────
 * The same connection carries a second event, `live`: the driver's own
 * sessions (energy and cost as they meter, not only status), payouts and
 * points, and availability changes at chargers the driver could see.
 * They are batched (at most one round a second) and invalidate the
 * queries lib/liveInvalidation.ts maps to their kind: stats, history,
 * sessions, map, location and charger views. The connected ack says
 * whether the Api's live bus is running; only then do live screens stop
 * polling (store/sessionStream.ts). After a reconnect everything live is
 * invalidated once, because events during the gap are gone. On an open
 * stream the Api also sends `degraded` when a bus stream is lost (live
 * screens poll again) and `recovered` once all are confirmed (polling
 * stops, everything live is invalidated once): Codex finding 8, 29 Oct 2026.
 *
 * SSR / test safety
 * ─────────────────
 * `EventSource` is a browser global. This module is only invoked from
 * main.tsx (which itself only runs in the browser); not import-safe
 * for SSR or Node tests without a shim. Acceptable: PWA isn't SSR'd.
 * What is tested sits in lib/liveInvalidation.ts: the health listeners
 * (listenLiveHealth, wired here once per connection) among them.
 */

const BASE_URL = import.meta.env['VITE_API_URL'] ?? '/api';

const RECONNECT_BACKOFF_INITIAL_MS = 1_000;
const RECONNECT_BACKOFF_MAX_MS = 30_000;

let eventSource: EventSource | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let currentBackoffMs = RECONNECT_BACKOFF_INITIAL_MS;
let queryClient: QueryClient | null = null;
let isInitialized = false;
/** A connected ack has arrived before: the next one is a reconnect. */
let connectedBefore = false;

/** About one refetch round a second, however many live events arrive. */
const LIVE_FLUSH_INTERVAL_MS = 1_000;
const liveBatcher = createLiveBatcher({
  intervalMs: LIVE_FLUSH_INTERVAL_MS,
  onFlush: (kinds) => {
    // Every matching query goes stale; the active ones refetch now.
    void queryClient?.invalidateQueries({ predicate: (q) => keyIsLiveFor(q.queryKey, kinds) });
  },
});

interface SessionUpdatePayload {
  type: 'session_update';
  operation: 'insert' | 'update' | 'replace';
  sessionId: string;
  userId: string;
  status: string | null;
  charger_id: string | null;
  emittedAt: string;
}

/**
 * One-shot initializer called from main.tsx after the QueryClient is
 * constructed. Subsequent calls are a no-op + warn. Wires up:
 *
 *   - auth-store subscription (login / logout / refresh handling)
 *   - initial connect if the persisted auth store already has a token
 */
export function initSessionEventStream(qc: QueryClient): void {
  if (isInitialized) {
    // eslint-disable-next-line no-console
    console.warn('[sessionEventStream] already initialized — skipping');
    return;
  }
  isInitialized = true;
  queryClient = qc;

  // Auth-store subscription. Zustand v5 listener signature:
  // (state, prevState) => void. We branch on accessToken transitions:
  //   null  → token : connect
  //   tokenA → tokenB : reconnect with fresh token
  //   token → null  : disconnect
  useAuthStore.subscribe((state, prevState) => {
    const tokenNow = state.accessToken;
    const tokenBefore = prevState.accessToken;
    if (tokenNow && tokenNow !== tokenBefore) {
      connect(tokenNow);
    } else if (!tokenNow && tokenBefore) {
      disconnect();
    }
  });

  // First-mount connect for the already-logged-in case (persisted
  // auth store carries the token through a page refresh).
  const initialToken = useAuthStore.getState().accessToken;
  if (initialToken) {
    connect(initialToken);
  }
}

function connect(token: string): void {
  // Tear down any existing connection first — covers the refresh case
  // and the defensive re-connect path after a reconnect-timer fire.
  disconnect();

  useSessionStreamStore.getState().setStatus('connecting');

  const url = `${BASE_URL}/me/session-events?token=${encodeURIComponent(token)}`;
  const es = new EventSource(url);
  eventSource = es;

  // Named events match the server's `event: <name>` framing — the
  // default `onmessage` only catches unnamed events.
  es.addEventListener('connected', (event: MessageEvent) => {
    useSessionStreamStore.getState().setStatus('open');
    // An older Api sends no `live`: no live events, keep polling.
    let live = false;
    try {
      live = (JSON.parse(event.data as string) as { live?: unknown }).live === true;
    } catch {
      // Malformed ack: treat as no live bus.
    }
    useSessionStreamStore.getState().setLive(live);
    // A reconnect: whatever changed during the gap is stale now.
    if (connectedBefore) liveBatcher.catchUp();
    connectedBefore = true;
    // Reset backoff on a successful handshake so a future failure
    // starts fresh at 1s instead of carrying the previous escalation.
    currentBackoffMs = RECONNECT_BACKOFF_INITIAL_MS;
    // eslint-disable-next-line no-console
    console.info('[sessionEventStream] connected');
  });

  es.addEventListener('session_update', (event: MessageEvent) => {
    handleSessionUpdate(event);
  });

  es.addEventListener('live', (event: MessageEvent) => {
    const e = parseLiveEvent(event.data as string);
    if (e) liveBatcher.add(e.kind);
  });

  // The live bus's health after the ack: `degraded` puts live screens back
  // on their poll, `recovered` takes them off it with one catch-up.
  listenLiveHealth(es, {
    isCurrent: () => eventSource === es,
    setLive: (live) => useSessionStreamStore.getState().setLive(live),
    catchUp: () => liveBatcher.catchUp(),
  });

  es.onerror = () => {
    useSessionStreamStore.getState().setStatus('error');
    // EventSource has 3 readyStates: CONNECTING (0), OPEN (1), CLOSED (2).
    // If CLOSED, the browser gave up — we schedule a manual reconnect.
    // If CONNECTING, the browser is auto-reconnecting natively; leave it.
    if (es.readyState === EventSource.CLOSED) {
      scheduleReconnect();
    }
  };
}

function handleSessionUpdate(event: MessageEvent): void {
  if (!queryClient) return;
  try {
    const data = JSON.parse(event.data as string) as SessionUpdatePayload;

    // Invalidate the ONE active-session query key. Every consumer (the
    // live home, ChargingPage, MiniChargingBar, QuickActions, the
    // dashboard) shares ACTIVE_SESSION_KEY from hooks/useActiveSession;
    // the second key ChargingPage used to poll is gone.
    queryClient.invalidateQueries({ queryKey: ['sessions', 'active'] });

    // Specific session detail page (SessionDetailPage uses
    // `['session', sessionId]`). Match the actual key, not the
    // pluralized variant.
    if (data.sessionId) {
      queryClient.invalidateQueries({ queryKey: ['session', data.sessionId] });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[sessionEventStream] failed to parse session_update', err);
  }
}

function scheduleReconnect(): void {
  // Coalesce overlapping reconnect attempts. The next fire ends up
  // using the next backoff tier regardless of how many onerrors land
  // during the gap.
  if (reconnectTimer) return;

  const delay = currentBackoffMs;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    const token = useAuthStore.getState().accessToken;
    if (token) {
      connect(token);
    } else {
      // Logged out during the backoff — drop to idle and stop trying.
      useSessionStreamStore.getState().setStatus('idle');
    }
  }, delay);

  // Escalate the backoff for the NEXT failure (not this scheduled
  // attempt). A successful `connected` ack resets it back to initial.
  currentBackoffMs = Math.min(currentBackoffMs * 2, RECONNECT_BACKOFF_MAX_MS);
}

function disconnect(): void {
  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  useSessionStreamStore.getState().setStatus('idle');
}
